import React, { useState, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight, RotateCcw } from 'lucide-react';
import { Button } from '../ui/button';
import { Machine } from '../../types/map';
import { MachineSelectionStep } from './MachineSelectionStep';
import { SKUSelectionStep } from './SKUSelectionStep';
import { RetailMediaSelectionStep } from './RetailMediaSelectionStep';
import { QuoteOverviewStep } from './QuoteOverviewStep';
import { useHeaderActions } from '../AppLayout';
import { useToast } from '../ui/use-toast';

const STORAGE_KEY = 'campaignWizardProgress';

interface SavedProgress {
  currentStep: number;
  selectedMachineIds: string[];
  skuCount: number;
  pricePerSKU: number;
  retailMediaSelections: Record<string, any>;
}

interface CampaignWizardProps {}

export function CampaignWizard({}: CampaignWizardProps) {
  const { toast } = useToast();
  const { setHeaderActions } = useHeaderActions();

  // Load saved progress from localStorage
  const loadSavedProgress = (): SavedProgress | null => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        return JSON.parse(saved);
      }
    } catch (error) {
      console.error('Error loading saved progress:', error);
    }
    return null;
  };

  const savedProgress = loadSavedProgress();

  const [currentStep, setCurrentStep] = useState(savedProgress?.currentStep || 1);
  const [selectedMachines, setSelectedMachines] = useState<Machine[]>([]);
  const [skuCount, setSkuCount] = useState(savedProgress?.skuCount || 0);
  const [pricePerSKU, setPricePerSKU] = useState(savedProgress?.pricePerSKU || 0);
  const [retailMediaSelections, setRetailMediaSelections] = useState<Record<string, any>>(
    savedProgress?.retailMediaSelections || {}
  );
  const [isAlreadyListed, setIsAlreadyListed] = useState(false);

  // Load selected machines from saved IDs
  useEffect(() => {
    if (savedProgress?.selectedMachineIds && savedProgress.selectedMachineIds.length > 0) {
      // Fetch machines by IDs
      fetch('/api/store/machines', { credentials: 'include' })
        .then(res => res.json())
        .then((machines: Machine[]) => {
          const savedMachines = machines.filter(m => 
            savedProgress.selectedMachineIds.includes(m.id)
          );
          setSelectedMachines(savedMachines);
        })
        .catch(err => console.error('Error loading saved machines:', err));
    }
  }, []);

  // Save progress to localStorage whenever state changes
  useEffect(() => {
    const progress: SavedProgress = {
      currentStep,
      selectedMachineIds: selectedMachines.map(m => m.id),
      skuCount,
      pricePerSKU,
      retailMediaSelections,
    };
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(progress));
    } catch (error) {
      console.error('Error saving progress:', error);
    }
  }, [currentStep, selectedMachines, skuCount, pricePerSKU, retailMediaSelections]);

  const handleNext = () => {
    if (currentStep < 4) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSkipToRetailMedia = () => {
    setIsAlreadyListed(true);
    setCurrentStep(3); // Skip to retail media (step 3), skipping SKU selection (step 2)
  };

  const canProceedToNextStep = () => {
    if (currentStep === 1) {
      return selectedMachines.length > 0 || isAlreadyListed;
    }
    if (currentStep === 2) {
      // SKU selection is optional, but if SKUs are selected, price must be set
      return skuCount === 0 || (skuCount > 0 && pricePerSKU > 0);
    }
    if (currentStep === 3) {
      // Check if at least one retail media item is selected
      return Object.keys(retailMediaSelections).some(key => {
        const selection = retailMediaSelections[key];
        if (!selection) return false;
        // Handle both number and empty string (BlankableNumber type)
        const machines = typeof selection.machines === 'number' ? selection.machines : (selection.machines === '' ? 0 : parseInt(selection.machines) || 0);
        const units = typeof selection.units === 'number' ? selection.units : (selection.units === '' ? 0 : parseInt(selection.units) || 0);
        return machines > 0 && units > 0;
      });
    }
    return true;
  };

  // Clear saved progress
  const clearSavedProgress = () => {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch (error) {
      console.error('Error clearing saved progress:', error);
    }
  };

  // Restart function to clear all selections
  const handleRestart = useCallback(() => {
    if (window.confirm('Are you sure you want to restart? This will clear all your selections across all steps.')) {
      clearSavedProgress();
      setCurrentStep(1);
      setSelectedMachines([]);
      setSkuCount(0);
      setPricePerSKU(0);
      setRetailMediaSelections({});
      
      toast({
        title: 'Restarted',
        description: 'All selections have been cleared. Starting fresh.',
      });
    }
  }, [toast]);

  // Update header actions with restart button
  useEffect(() => {
    setHeaderActions(
      <Button
        variant="outline"
        size="lg"
        onClick={handleRestart}
        className="flex items-center gap-2"
      >
        <RotateCcw className="w-4 h-4" />
        <span className="hidden sm:inline">Restart</span>
      </Button>
    );

    // Cleanup: clear header actions when component unmounts
    return () => {
      setHeaderActions(null);
    };
  }, [handleRestart, setHeaderActions]);

  const stepTitles = [
    'Select Machines',
    'Select SKUs',
    'Choose Media',
    'Review & Quote'
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200 py-4">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Campaign Builder</h1>
              <p className="text-sm text-gray-600 mt-1">Create a new campaign quote</p>
            </div>
          </div>
        </div>
      </div>

      {/* Progress Indicator */}
      <div className="bg-white border-b border-gray-200">
        <div className="container mx-auto px-4 py-6">
          <div className="relative flex items-center w-full">
            {/* Connecting lines */}
            <div className="absolute top-6 left-0 right-0 h-1 flex">
              <div className={`flex-1 h-full ${currentStep > 1 ? 'bg-green-500' : 'bg-gray-200'}`} />
              <div className={`flex-1 h-full ${currentStep > 2 ? 'bg-green-500' : 'bg-gray-200'}`} />
              <div className={`flex-1 h-full ${currentStep > 3 ? 'bg-green-500' : 'bg-gray-200'}`} />
            </div>
            
            {/* Steps */}
            <div className="relative flex items-center justify-between w-full">
              {stepTitles.map((title, index) => {
                const stepNum = index + 1;
                const isActive = currentStep === stepNum;
                const isCompleted = currentStep > stepNum;
                
                return (
                  <div key={stepNum} className="flex flex-col items-center flex-1 relative z-10">
                    <div
                      className={`w-12 h-12 rounded-full flex items-center justify-center font-semibold text-lg transition-colors mb-2 border-4 border-white ${
                        isActive
                          ? 'bg-blue-600 text-white'
                          : isCompleted
                          ? 'bg-green-500 text-white'
                          : 'bg-gray-200 text-gray-600'
                      }`}
                    >
                      {isCompleted ? '✓' : stepNum}
                    </div>
                    <div className="text-center">
                      <div className={`text-sm font-medium ${isActive ? 'text-blue-600' : isCompleted ? 'text-green-600' : 'text-gray-600'}`}>
                        Step {stepNum}
                      </div>
                      <div className={`text-xs mt-1 ${isActive ? 'text-blue-600' : isCompleted ? 'text-green-600' : 'text-gray-500'}`}>
                        {title}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Step Content */}
      <div className="container mx-auto px-4 py-8">
        {currentStep === 1 && (
          <MachineSelectionStep
            selectedMachines={selectedMachines}
            onSelectionChange={setSelectedMachines}
            onSkipToRetailMedia={handleSkipToRetailMedia}
          />
        )}
        
        {currentStep === 2 && (
          <SKUSelectionStep
            selectedMachines={selectedMachines}
            skuCount={skuCount}
            pricePerSKU={pricePerSKU}
            onSKUCountChange={setSkuCount}
            onPricePerSKUChange={setPricePerSKU}
            isAlreadyListed={isAlreadyListed}
          />
        )}
        
        {currentStep === 3 && (
          <RetailMediaSelectionStep
            selectedMachines={selectedMachines}
            retailMediaSelections={retailMediaSelections}
            onRetailMediaSelectionsChange={setRetailMediaSelections}
            isAlreadyListed={isAlreadyListed}
            skuCount={skuCount}
          />
        )}
        
        {currentStep === 4 && (
          <QuoteOverviewStep
            selectedMachines={selectedMachines}
            skuCount={skuCount}
            pricePerSKU={pricePerSKU}
            retailMediaSelections={retailMediaSelections}
          />
        )}

        {/* Navigation Buttons */}
        <div className="flex items-center justify-between mt-8 pt-6 border-t border-gray-200">
          <Button
            variant="outline"
            onClick={handleBack}
            disabled={currentStep === 1}
            className="flex items-center gap-2"
          >
            <ChevronLeft className="w-4 h-4" />
            Back
          </Button>
          
          {currentStep < 4 && (
            <Button
              onClick={handleNext}
              disabled={!canProceedToNextStep()}
              className="flex items-center gap-2"
            >
              Next
              <ChevronRight className="w-4 h-4" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

